import { Component, HostListener, Input, OnDestroy, OnInit } from "@angular/core";
import { SimpleModalComponent } from "ngx-simple-modal";
import { TranslateService } from "@ngx-translate/core";
import { select } from "@angular-redux/store";
import { Observable, Subject } from "rxjs";
import { take, takeUntil } from "rxjs/operators";
import * as moment from "moment";

import { PlanningBlock } from "../../../../pb-screen/models/planning-block/planning-block.interface";
import { TransportOrder } from "../../../../pb-screen/models/transport-order/transport-order.interface";
import { ToActions } from "../../../../to-screen/to.actions";
import { CapacitySlot } from "../../../../pb-screen/models/capacity-slot.interface";
import { Driver } from "../../../../static/models/driver.interface";
import { Haulier } from "../../../../pb-screen/models/haulier.interface";
import { CheckDateCompatabilityService } from "../../../../services/planning-logic/check-date-compatability.service";
import { SharedService } from "../../../services/shared.service";
import { ConfirmDialogService } from "../../confirm-dialog/confirm-dialog.service";
import { environment } from "../../../../../environments/environment";

export interface ReplanModalInput {
  title?: string;
  planningBlocks: PlanningBlock[];
  message?: string;
  confirmButtonTextKey?: string;
}

export interface ReplanModalResult {
  confirmed: boolean;
  targetTransportOrder?: TransportOrder;
  changePBDate?: boolean;
  planDate? : Date;
  createNewTo?: boolean;
  newToPlanning?: ReplanNewToPlanningRequest;
  createdTransportOrderIds?: string[];
  createdPlanningBlocks?: PlanningBlock[];
  sendAfterPreplan?: boolean;
}

export interface ReplanNewToPlanningRequest {
  capacitySlot?: CapacitySlot;
  driver?: Driver;
  haulier?: Haulier;
  planDate: Date;
  finalizedCombi: boolean;
  targetPlanningGroup: string;
  sendAfterPreplan: boolean;
  changePlanningBlockDate: boolean;
}

@Component({
  selector: 'planningactions-replan-modal',
  templateUrl: './planningactions-replan-modal.component.html',
  styleUrls: ['./planningactions-replan-modal.component.scss']
})
export class PlanningactionsReplanModalComponent extends SimpleModalComponent<ReplanModalInput, ReplanModalResult> implements OnInit, OnDestroy, ReplanModalInput {

  @Input() title?: string;
  @Input() planningBlocks: PlanningBlock[] = [];
  @Input() message?: string;
  @Input() confirmButtonTextKey?: string;

  @select("openTransportOrders") public openTransportOrders$!: Observable<TransportOrder[]>;
  @select("filteredOpenTransportOrders") public filteredOpenTransportOrders$!: Observable<TransportOrder[]>;

  // view state
  loading = false;
  errorMessage: string | null = null;
  selectedTransportOrder: TransportOrder | null = null;
  showNewToPlanning = false;
  selectedCapacity: CapacitySlot;
  selectedDriver: Driver;
  selectedHaulier: Haulier;
  planDate: Date = new Date();
  finalizedCombi = false;
  planningBlocksIncompatibleWarning = false;
  planningDateNotCompatibleWarning = false;
  destroyed$ = new Subject<void>();
  private pendingSendAfterPreplan: boolean | null = null;

  // simple filter box state (parity with transport-orders-master-detail-filter)
  public columnFields: string[] = [];
  public filterValue = "";

  // grid settings (single selection)
  public selectableSettings = { checkboxOnly: false, mode: 'single' } as any;
  public mySelection: any[] = [];

  constructor(
    private readonly translate: TranslateService,
    private readonly toActions: ToActions,
    private readonly checkDateCompatibilityService: CheckDateCompatabilityService,
    private readonly sharedService: SharedService,
    private readonly confirmDialogService: ConfirmDialogService
  ) {
    super();
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.cancel();
    }
  }

  ngOnInit(): void {
    // ensure we have TOs
    this.toActions.getAllOpenTransportOrders();

    // setup filterable columns like transport-order-master-detail
    this.columnFields = [
      'id',
      'date',
      'startAddress',
      'endAddress',
      'haulierName',
      'firstTruckDescription',
      'driver'
    ];

    // defensive default title
    if (!this.title) {
      this.translate.get('REPLAN_MODAL.TITLE').pipe(takeUntil(this.destroyed$)).subscribe(trans => this.title = trans || 'Replan to existing Transport Order');
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onTransportOrderSelectionChange(event: any): void {
    try {
      const selected = event.selectedRows && event.selectedRows[0] && event.selectedRows[0].dataItem;
      this.selectedTransportOrder = selected || null;
      this.mySelection = this.selectedTransportOrder ? [this.selectedTransportOrder.id] : [];
    } catch {
      this.selectedTransportOrder = null;
      this.mySelection = [];
    }
  }

  // filtering copied behavior: dispatch store filter on current filterValue
  public filterOpenTransportOrders(): void {
    this.toActions.filterOpenTransportOrders(this.filterValue, this.columnFields);
  }

  public resetFilter(): void {
    this.toActions.resetOpenTransportOrdersFilter();
    this.filterValue = "";
  }

  public showExistingToMode(): void {
    this.resetPendingNewToValidation();
    this.showNewToPlanning = false;
  }

  public showNewToMode(): void {
    this.resetPendingNewToValidation();
    this.showNewToPlanning = true;
    this.selectedTransportOrder = null;
    this.mySelection = [];
  }

  public getSelectedPlanDate(planDate: string): void {
    this.resetPendingNewToValidation();
    this.planDate = new Date(planDate);
  }

  public getSelectedCapacity(capacity: CapacitySlot): void {
    this.resetPendingNewToValidation();
    this.selectedCapacity = capacity;
  }

  public getSelectedDriver(driver: Driver): void {
    this.resetPendingNewToValidation();
    this.selectedDriver = driver;
  }

  public getSelectedHaulier(haulier: Haulier): void {
    this.resetPendingNewToValidation();
    this.selectedHaulier = haulier;
  }

  public getSelectedFinalizeCombi(finalizedCombi: boolean): void {
    this.finalizedCombi = finalizedCombi;
  }

  canConfirm(): boolean {
    return !!(this.planningBlocks && this.planningBlocks.length > 0 && this.selectedTransportOrder);
  }

  public canPlanNewTo(): boolean {
    return !!(this.planningBlocks && this.planningBlocks.length > 0) &&
      !!(this.selectedCapacity || this.selectedDriver?.id || this.selectedHaulier?.id);
  }

  public hasValidationWarning(): boolean {
    return this.planningBlocksIncompatibleWarning || this.planningDateNotCompatibleWarning;
  }

  confirmReplan(): void {
    if (!this.canConfirm() || !this.selectedTransportOrder) { return; }

    this.errorMessage = null;
    this.loading = true;

    const targetTo = this.selectedTransportOrder;
    const changePBDate = true;
    const planDate = targetTo && (targetTo as any).date ? new Date((targetTo as any).date) : new Date();

    this.result = {
      confirmed: true,
      targetTransportOrder: targetTo,
      changePBDate: changePBDate,
      planDate: planDate};
    this.loading = false;
    this.close().then();

  }

  confirmCreateNewTo(): void {
    this.showNewToMode();
  }

  public confirmPlanNewTo(sendAfterPreplan: boolean): void {
    if (!this.canPlanNewTo() || this.loading || this.hasValidationWarning()) { return; }

    this.errorMessage = null;
    this.pendingSendAfterPreplan = sendAfterPreplan;
    this.validatePlanningGroup();
  }

  public getPlanningBlocksPGIncompatible(): string {
    const targetPlanningGroup = this.getTargetPlanningGroup();
    return (this.planningBlocks || [])
      .filter(pb => pb.planningGroup !== targetPlanningGroup)
      .map(pb => pb.id)
      .join();
  }

  public confirmPlanningGroupIncompatibility(): void {
    this.planningBlocksIncompatibleWarning = false;
    this.validatePlanningDate();
  }

  public cancelPlanningGroupIncompatibility(): void {
    this.resetPendingNewToValidation();
  }

  public confirmPlanningDateNotCompatible(): void {
    this.planningDateNotCompatibleWarning = false;
    this.validateDriverAbsence(true);
  }

  public ignorePlanningBlockDateChange(): void {
    this.planningDateNotCompatibleWarning = false;
    this.validateDriverAbsence(false);
  }

  public cancelPlanningDateNotCompatible(): void {
    this.resetPendingNewToValidation();
  }

  private validatePlanningGroup(): void {
    const targetPlanningGroup = this.getTargetPlanningGroup();
    const compatible = environment.disablePlanningGroupsCompatibilityCheck ||
      !targetPlanningGroup ||
      (this.planningBlocks || []).every(pb => pb.planningGroup === targetPlanningGroup);

    if (!compatible) {
      this.planningBlocksIncompatibleWarning = true;
      return;
    }

    this.validatePlanningDate();
  }

  private validatePlanningDate(): void {
    const defaultPlanDate = moment.utc(this.planDate).local();
    const dateIsCompatible = this.checkDateCompatibilityService
      .forPlanningOnSingleDate(defaultPlanDate, this.planningBlocks || []);

    if (!dateIsCompatible) {
      this.planningDateNotCompatibleWarning = true;
      return;
    }

    this.validateDriverAbsence(false);
  }

  private validateDriverAbsence(changePlanningBlockDate: boolean): void {
    const effectiveDriver = this.selectedDriver?.id
      ? this.selectedDriver
      : this.selectedCapacity?.driver?.id
        ? this.selectedCapacity.driver
        : null;

    if (!effectiveDriver) {
      this.completeNewToPlanning(changePlanningBlockDate);
      return;
    }

    this.loading = true;
    const formattedPlanDate = moment(this.planDate).startOf("day").format("YYYY-MM-DD");
    this.sharedService.checkDriverAbsence(formattedPlanDate, effectiveDriver.id).pipe(take(1)).subscribe(
      absences => {
        if (this.pendingSendAfterPreplan === null) { return; }

        if (!absences || !absences.length) {
          this.completeNewToPlanning(changePlanningBlockDate);
          return;
        }

        this.translate.get("SHARED.DRIVER_ABSENT_ON_SELECTED_DATE", {
          driverName: effectiveDriver.name,
          fromDate: this.prettifyDate(absences[0].fromDate),
          toDate: this.prettifyDate(absences[0].toDate)
        }).pipe(take(1)).subscribe(driverAbsentMessage => {
          if (this.pendingSendAfterPreplan === null) { return; }

          this.confirmDialogService.confirmThis(
            driverAbsentMessage,
            () => this.completeNewToPlanning(changePlanningBlockDate),
            () => this.resetPendingNewToValidation()
          );
        });
      },
      () => {
        if (this.pendingSendAfterPreplan === null) { return; }

        this.loading = false;
        this.pendingSendAfterPreplan = null;
        this.translate.get("TOASTR_MESSAGES.ERROR.PLAN_FAILED").pipe(take(1)).subscribe(message => {
          this.errorMessage = message || "Planning failed!";
        });
      }
    );
  }

  private completeNewToPlanning(changePlanningBlockDate: boolean): void {
    if (this.pendingSendAfterPreplan === null) { return; }

    const sendAfterPreplan = this.pendingSendAfterPreplan;

    this.result = {
      confirmed: true,
      createNewTo: true,
      sendAfterPreplan,
      newToPlanning: {
        capacitySlot: this.selectedCapacity,
        driver: this.selectedDriver,
        haulier: this.selectedHaulier,
        planDate: this.planDate,
        finalizedCombi: this.finalizedCombi,
        targetPlanningGroup: this.getTargetPlanningGroup(),
        sendAfterPreplan,
        changePlanningBlockDate
      }
    };
    this.pendingSendAfterPreplan = null;
    this.loading = false;
    this.close().then();
  }

  private resetPendingNewToValidation(): void {
    this.pendingSendAfterPreplan = null;
    this.planningBlocksIncompatibleWarning = false;
    this.planningDateNotCompatibleWarning = false;
    this.loading = false;
  }

  private prettifyDate(date: any): string {
    const navisionNullDate = moment("1760-01-01");
    const parsedDate = moment(date);
    return parsedDate.isValid() && parsedDate.isAfter(navisionNullDate)
      ? parsedDate.format("DD-MM-YY HH:mm")
      : "";
  }

  private getTargetPlanningGroup(): string {
    if (this.selectedCapacity?.truck?.planningGroupID) {
      return this.selectedCapacity.truck.planningGroupID;
    }
    return "";
  }

  cancel(): void {
    this.resetPendingNewToValidation();
    this.result = { confirmed: false };
    this.close().then();
  }
}
